import { useFeatureToggle } from '../../feature-flags/useFeatureToggle';
import { AgentType } from '@server/ai';
import { AI_AGENTS_INFO, AiAgentWithFacts, SUPPORTED_TYPES_FOR_CREATION } from './aiAgentsConsts';
import usePermission from '../common/permissions/usePermission';
import useAiAgents from './useAiAgents';
import useAgentsSubscriptions from './useAgentsSubscriptions';

/**
 * Determines which agent types can be created based on user permissions, feature toggles, and subscription plan.
 * Handles complex replacement logic where newer agent types can replace older ones.
 * Separates agents into those available with current subscription vs those requiring upgrade.
 *
 * @param isAllowedCreate - Whether the user has permission to create agents
 * @param agentsFeatureToggles - Feature toggle states for each agent type (controls feature release)
 * @param agentsFeaturePlans - Subscription plan availability for each agent type
 * @returns Object containing creatable agents (with subscription), agents requiring upgrade, and button visibility flag
 */
const getCreatableAgents = (
  isAllowedCreate: boolean,
  agentsFeatureToggles: Record<AgentType, boolean>,
  agentsFeaturePlans: Record<AgentType, boolean>,
) => {
  const creatableAgentTypes: AgentType[] = [];
  const creatableAgentTypesNoSub: AgentType[] = [];

  SUPPORTED_TYPES_FOR_CREATION.forEach((agentType) => {
    if (!agentsFeatureToggles[agentType]) {
      return;
    }
    const isEnabled = agentsFeaturePlans[agentType];
    const { isReplacedBy } = AI_AGENTS_INFO[agentType];

    // Handle legacy agent types that are being replaced by newer versions
    if (isReplacedBy) {
      const isNewTypeOpenedAndEnabled =
        agentsFeatureToggles[isReplacedBy] && agentsFeaturePlans[isReplacedBy];
      // Only show the old type if it's enabled but the new replacement type isn't yet enabled
      if (isEnabled && !isNewTypeOpenedAndEnabled) {
        creatableAgentTypes.push(agentType);
      }
      return;
    }

    // Check if this agent type is replacing another one
    const isReplacingType = Object.keys(AI_AGENTS_INFO).find(
      (key) => AI_AGENTS_INFO[key as AgentType]?.isReplacedBy === agentType,
    );

    if (isReplacingType) {
      const isReplacingTypeOpenedAndEnabled =
        agentsFeatureToggles[isReplacingType as AgentType] &&
        agentsFeaturePlans[isReplacingType as AgentType];
      // If the old type is enabled but this new type isn't, let the old type handle creation
      if (isReplacingTypeOpenedAndEnabled && !isEnabled) {
        return;
      }
    }

    // Categorize based on subscription plan: agents with subscription vs requiring upgrade
    if (isEnabled) {
      creatableAgentTypes.push(agentType);
    } else {
      creatableAgentTypesNoSub.push(agentType);
    }
  });

  const canCreateNewAgents = isAllowedCreate && creatableAgentTypes.length > 0;

  return { creatableAgentTypes, creatableAgentTypesNoSub, canCreateNewAgents };
};

/**
 * Categorizes existing agent instances based on their type, feature toggle status, and subscription plan.
 * Separates singleton agents (one per type) from instantiated agents (multiple allowed).
 * Further separates singleton agents based on subscription availability.
 *
 * @param agents - Array of existing agent instances
 * @param agentsFeatureToggles - Feature toggle states for each agent type (controls feature release)
 * @param agentsFeaturePlans - Subscription plan availability for each agent type
 * @returns Object containing categorized agent arrays: instantiated (always shown), singletons with subscription, and singletons requiring upgrade
 */
const getExistingAgents = (
  agents: AiAgentWithFacts[],
  agentsFeatureToggles: Record<AgentType, boolean>,
  agentsFeaturePlans: Record<AgentType, boolean>,
) => {
  const instantiatedAgents: AiAgentWithFacts[] = [];
  const singletonAgents: AiAgentWithFacts[] = [];
  const singletonAgentsNoSub: AiAgentWithFacts[] = [];

  agents.forEach((agent) => {
    // Singleton agents can only have one instance per type and cannot be created through normal flow
    const isSingletonAgent = !AI_AGENTS_INFO[agent.type].createEntryPoint.isAllowed;

    //Feature toggle off - means the feature is not released yet
    if (!agentsFeatureToggles[agent.type]) {
      return;
    }
    if (isSingletonAgent) {
      // Categorize singleton agents based on subscription: available vs requiring upgrade
      if (agentsFeaturePlans[agent.type]) {
        singletonAgents.push(agent);
      } else {
        singletonAgentsNoSub.push(agent);
      }
    } else {
      // Instantiated agents are always shown regardless of sub since they already exist
      instantiatedAgents.push(agent);
    }
  });

  return {
    instantiatedAgents,
    singletonAgents,
    singletonAgentsNoSub,
  };
};

/**
 * Main hook that orchestrates agent rendering logic by combining creation permissions,
 * feature toggles, subscription plan features, and existing agent instances to determine what should be displayed in the UI.
 *
 * The hook categorizes agents into multiple groups based on:
 * - Feature toggle status (controls feature release to users)
 * - Subscription plan availability (controls access based on user's plan)
 * - Agent creation permissions
 * - Existing agent instances and their singleton/instantiated nature
 *
 * @returns Object containing all agent categories and UI state flags:
 *   - existingAgents: Agents already created and available with current subscription
 *   - singletonAgentsNoSub: Existing singleton agents that require subscription upgrade
 *   - creatableAgentTypes: Agent types that can be created with current subscription
 *   - creatableAgentTypesNoSub: Agent types available but requiring subscription upgrade
 *   - showNewAgentsButton: Whether to show the create new agent button
 */
const useAgentsToRender = () => {
  const { agents } = useAiAgents();
  const agentsFeaturePlans = useAgentsSubscriptions();
  const [isAllowedCreate] = usePermission(['create:ai-agent']);

  // Gather all relevant feature toggle states for agent types
  const {
    permissionsAgent,
  } = useFeatureToggle();

  if (!agents) {
    return undefined;
  }

  const agentsFeatureToggles: Record<AgentType, boolean> = {
    [AgentType.Documentation]: true,
    [AgentType.ProcessOptimization]: true,
    [AgentType.Monitoring]: false, // Always disabled - legacy agent
    [AgentType.MonitoringV2]: true,
    [AgentType.Permissions]: permissionsAgent,
  };

  // Categorize existing agent instances
  const { instantiatedAgents, singletonAgents, singletonAgentsNoSub } = getExistingAgents(
    agents,
    agentsFeatureToggles,
    agentsFeaturePlans,
  );

  // Combine visible existing agents for display (singletonAgents appear first)
  const existingAgents = singletonAgents.concat(instantiatedAgents);

  // Determine what new agents can be created
  const { creatableAgentTypes, creatableAgentTypesNoSub, canCreateNewAgents } = getCreatableAgents(
    isAllowedCreate,
    agentsFeatureToggles,
    agentsFeaturePlans,
  );

  return {
    existingAgents,
    singletonAgentsNoSub,
    creatableAgentTypes,
    creatableAgentTypesNoSub,
    canCreateNewAgents,
  };
};

export default useAgentsToRender;
